import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["dataTestId", "onClick", "expandableContent", "expandableTriggerText", "shouldVisuallyHideTriggerText", "expandableContentHasBackgroundColor", "expandableRowHasTopBorder", "aria-label", "actionLabel", "disabled", "disabledActionLabel"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { memo, useMemo } from 'react';
import { Button, ButtonSize, ButtonVariant } from "../../button";
import { IconChevronRight, IconMinus } from "../../icons";
import { buildDataTestProps } from "../../utils";
import { ExpandableRowsByColumn, ExpandableRowsByRows, ExpandableTriggerButton, getMaxExpandableRows } from "../expandable";
import { TdElement as Td, ThElement as Th, TrElement as Tr } from "../html";
import { CellType } from "../interfaces";
import { applyAccessor } from "../utils";
import { ActionCellTd, DisabledActionButton } from "./helperComponents";
import { jsx as ___EmotionJSX } from "@emotion/core";
var TableRow = /*#__PURE__*/memo(function (_ref) {
  var tableRowKey = _ref.tableRowKey,
      data = _ref.data,
      index = _ref.index,
      columns = _ref.columns,
      attributes = _ref.attributes,
      isStriped = _ref.isStriped,
      isExpanded = _ref.isExpanded,
      toggleExpanded = _ref.toggleExpanded,
      actionColumnIndex = _ref.actionColumnIndex,
      isSorted = _ref.isSorted,
      spacing = _ref.spacing;

  var dataTestId = attributes.dataTestId,
      onClick = attributes.onClick,
      getRowExpandableContent = attributes.expandableContent,
      expandableTriggerText = attributes.expandableTriggerText,
      shouldVisuallyHideTriggerText = attributes.shouldVisuallyHideTriggerText,
      expandableContentHasBackgroundColor = attributes.expandableContentHasBackgroundColor,
      expandableRowHasTopBorder = attributes.expandableRowHasTopBorder,
      ariaLabel = attributes['aria-label'],
      actionLabel = attributes.actionLabel,
      disabled = attributes.disabled,
      disabledActionLabel = attributes.disabledActionLabel,
      trProps = _objectWithoutProperties(attributes, _excluded);

  var dataTestProps = buildDataTestProps({
    dataTestComponent: 'StencilTableRow',
    dataTestId: dataTestId
  });
  var isExpandable = !!toggleExpanded && !onClick;
  var isActionCustomPosition = !!(actionColumnIndex || actionColumnIndex === 0);

  if (isStriped && index % 2 === 0) {
    isStriped = false;
  }

  var handleClick = !!onClick && !disabled ? function (event) {
    return onClick({
      data: data,
      index: index,
      event: event
    });
  } : undefined;
  var handleActionButtonClick = !!onClick && !disabled ? function (event) {
    event.stopPropagation();
    onClick({
      data: data,
      index: index,
      event: event
    });
  } : undefined;
  var handleToggleExpanded = !!toggleExpanded ? function () {
    return toggleExpanded(index);
  } : undefined;
  var baseColumns = columns.map(function (_ref2, colIndex) {
    var accessor = _ref2.accessor,
        CellContent = _ref2.cellComponent,
        textAlign = _ref2.textAlign,
        verticalAlign = _ref2.verticalAlign,
        width = _ref2.width,
        hasReducedSpacing = _ref2.hasReducedSpacing,
        _ref2$dataCellType = _ref2.dataCellType,
        dataCellType = _ref2$dataCellType === void 0 ? CellType.Data : _ref2$dataCellType;
    var cellData = applyAccessor(accessor, data, index);
    var isCellTypeHeader = dataCellType === CellType.Header;
    var Cell = isCellTypeHeader ? Th : Td;
    var dataTypeCellProps = isCellTypeHeader ? {
      scope: 'row'
    } : {};
    var cellKey = tableRowKey !== undefined ? tableRowKey + colIndex : colIndex;
    return ___EmotionJSX(Cell, _extends({
      key: cellKey,
      isStriped: isStriped,
      textAlign: textAlign,
      verticalAlign: verticalAlign,
      width: width,
      "aria-label": ariaLabel,
      hasReducedSpacing: hasReducedSpacing
    }, dataTypeCellProps), !!CellContent ? ___EmotionJSX(CellContent, {
      data: cellData,
      index: index
    }) : cellData);
  }); // Used to calculate the maximal number of expanded rows to add using the column-based config.

  var maxExpandableRows = useMemo(function () {
    return isExpandable && !getRowExpandableContent ? getMaxExpandableRows(columns, data, index) : 0;
  }, [columns, data, index, isExpandable, getRowExpandableContent]);

  var ExpandableCommonProps = _objectSpread({
    data: data,
    index: index,
    isExpanded: isExpanded,
    isStriped: isStriped,
    expandableContentHasBackgroundColor: expandableContentHasBackgroundColor,
    expandableRowHasTopBorder: expandableRowHasTopBorder,
    isSorted: isSorted,
    spacing: spacing,
    columns: columns
  }, trProps);

  var renderDisabledActionButton = function renderDisabledActionButton() {
    return ___EmotionJSX(DisabledActionButton, {
      "aria-disabled": true,
      "aria-label": disabledActionLabel,
      icon: ___EmotionJSX(IconMinus, {
        title: actionLabel
      }),
      size: ButtonSize.Small,
      variant: ButtonVariant.Tertiary
    });
  };

  var renderActionButton = function renderActionButton() {
    return ___EmotionJSX(Button, {
      icon: ___EmotionJSX(IconChevronRight, {
        title: actionLabel
      }),
      onClick: handleActionButtonClick,
      size: ButtonSize.Small,
      variant: ButtonVariant.Tertiary
    });
  };

  var renderExpandButton = function renderExpandButton() {
    return ___EmotionJSX(ExpandableTriggerButton, {
      isExpanded: isExpanded,
      toggleExpanded: handleToggleExpanded,
      shouldVisuallyHideTriggerText: shouldVisuallyHideTriggerText,
      triggerText: expandableTriggerText
    });
  };

  return ___EmotionJSX(React.Fragment, null, ___EmotionJSX(Tr, _extends({
    onClick: handleClick,
    isClickable: !!onClick,
    disabled: disabled,
    hasBackgroundColor: isStriped
  }, dataTestProps, trProps), isActionCustomPosition ? baseColumns.slice(0, actionColumnIndex) : baseColumns, !!onClick && ___EmotionJSX(ActionCellTd, {
    hasReducedSpacing: true
  }, disabled ? renderDisabledActionButton() : renderActionButton()), isExpandable && !!toggleExpanded && ___EmotionJSX(Td, {
    hasReducedSpacing: true,
    textAlign: "right"
  }, disabled ? renderDisabledActionButton() : renderExpandButton()), isActionCustomPosition ? baseColumns.slice(actionColumnIndex) : null), isExpandable && !disabled && !!getRowExpandableContent ? ___EmotionJSX(ExpandableRowsByRows, _extends({}, ExpandableCommonProps, {
    expandableContent: getRowExpandableContent
  })) : ___EmotionJSX(ExpandableRowsByColumn, _extends({}, ExpandableCommonProps, {
    maxExpandableRows: maxExpandableRows,
    actionColumnIndex: actionColumnIndex,
    isActionCustomPosition: isActionCustomPosition,
    ariaLabel: ariaLabel
  })));
});
TableRow.displayName = 'TableRow';
export default TableRow;